import * as React from "react";
import { Stack, Heading } from "@origin-digital/ods-core";
import FormFieldLayout from "../FormComponents/FormFieldLayout";
import { useFormikContext } from "formik";
import { ChangeRequestFormValues } from "./formValue";
import ReadOnlyFieldWrapper from "../ReadOnlyFieldWrapper";
import ReadOnlyTextField from "../FormComponents/ReadOnlyTextField";
import {
  typeOfChangesOptions,
  offPeakOptions,
  getFullName,
  getPhaseLabel,
  getShowFieldsForChangeTypes,
  mapPostSubmissionMeterProviderToLabel,
} from "./helpers";
import ReadOnlyUploadedFile from "../FormComponents/ReadOnlyUploadedFile";
import { useContext, useEffect, useMemo } from "react";
import RequestorInfo from "../RequestorInfo";
import ChangeRequestContext from "src/context/ChangeRequestContext";

const ChangeRequestElectricityBlockReadonly: React.FC = () => {
  const { values } = useFormikContext<ChangeRequestFormValues>();
  const { setSectionVisibility, sectionVisibility } =
    useContext(ChangeRequestContext);

  const getChangeTypesLabel = (changeTypes: string[]) => {
    return changeTypes
      .map(
        (type) => typeOfChangesOptions.find((opt) => opt.value === type)?.label
      )
      .filter(Boolean)
      .join(", ");
  };

  const getOffPeakRequirementsLabel = (requirements: string[]) => {
    return requirements
      .map((req) => offPeakOptions.find((opt) => opt.value === req)?.label)
      .filter(Boolean)
      .join(", ");
  };

  const state = values.premise?.site_details?.state ?? "";

  const showFields = useMemo(
    () =>
      getShowFieldsForChangeTypes({
        state,
        changeTypes: values.changeTypes,
      }),
    [state, values.changeTypes]
  );

  useEffect(() => {
    setSectionVisibility({
      meteringRequirementsSection:
        showFields.showMeterProvider || showFields.showOffPeakRequirements,
      supportingDocumentsSection:
        showFields.showRexId ||
        showFields.showCoesReferenceNumber ||
        showFields.showCoesFiles ||
        showFields.showEwrFiles ||
        !!values.networkFormNumber,
      solarInstallationDetailsSection:
        showFields.showSolarInstallationDetails ||
        showFields.showInstallationDate,
    });
  }, [showFields, values.networkFormNumber]);

  return (
    <Stack
      space={{
        xs: "xlarge",
        md: "xxlarge",
      }}
    >
      {/* Change type section */}
      <ChangeRequestSection id="changeTypeSection" visible={true}>
        <FormFieldLayout multipleFields>
          <Heading variant="h4">Change type</Heading>
          <ReadOnlyTextField
            label="Type of changes required"
            value={getChangeTypesLabel(values.changeTypes)}
          />
        </FormFieldLayout>
      </ChangeRequestSection>

      {/* Licensed electrician section */}
      <ChangeRequestSection id="electricianSection" visible={true}>
        <FormFieldLayout multipleFields>
          <Heading variant="h4">Licensed electrician</Heading>
          <ReadOnlyTextField
            label="Name"
            value={getFullName(
              values.electricianFields.firstName ?? "",
              values.electricianFields.lastName ?? ""
            )}
          />
          {values.noCompany && (
            <ReadOnlyTextField
              label="Company name"
              value={values.electricianFields.companyName ?? ""}
            />
          )}
          <ReadOnlyTextField
            label="Phone number"
            value={values.electricianFields.phoneNumber ?? ""}
          />
          <ReadOnlyTextField
            label="Licence number"
            value={values.electricianFields.licenceNumber ?? ""}
          />
        </FormFieldLayout>
      </ChangeRequestSection>

      {/* Supply details section */}
      <ChangeRequestSection id="supplyDetailsSection" visible={true}>
        <ReadOnlyFieldWrapper value={values.supplyDetails}>
          <FormFieldLayout multipleFields>
            <Heading variant="h4">Supply details</Heading>
            {showFields.showSupplyType && (
              <ReadOnlyTextField
                label="Required supply details"
                value={
                  values.supplyDetails.supplyType === "overhead"
                    ? "Overhead"
                    : "Underground"
                }
              />
            )}
            <ReadOnlyTextField
              label="Max load, Amps"
              value={values.supplyDetails.maxLoad}
            />
            <ReadOnlyTextField
              label="Required supply phases"
              value={getPhaseLabel(values.supplyDetails.numberOfPhases)}
            />
            {values.supplyDetails.transformer && (
              <ReadOnlyTextField
                label="Current transformer"
                value="Current transformer (CT metering) required"
              />
            )}
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>
      </ChangeRequestSection>

      {/* Metering requirements section */}
      <ChangeRequestSection
        id="meteringRequirementsSection"
        visible={sectionVisibility.meteringRequirementsSection}
      >
        <ReadOnlyFieldWrapper value={values.meteringRequirements}>
          <FormFieldLayout multipleFields>
            <Heading variant="h4">Metering requirements</Heading>
            {showFields.showMeterProvider && (
              <ReadOnlyTextField
                label="Meter provider"
                value={mapPostSubmissionMeterProviderToLabel(
                  values.meteringRequirements.meterProvider
                )}
              />
            )}
            {showFields.showOffPeakRequirements &&
              values.meteringRequirements.offPeakRequirements.length > 0 && (
                <ReadOnlyTextField
                  label="Off-peak requirements"
                  value={getOffPeakRequirementsLabel(
                    values.meteringRequirements.offPeakRequirements
                  )}
                />
              )}
            {values.meteringRequirements.otherOffPeakRequirements && (
              <ReadOnlyTextField
                //otherOffPeakRequirements is actually offPeakRequirements post submission
                label="Off-peak requirements"
                value={values.meteringRequirements.otherOffPeakRequirements}
              />
            )}
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>
      </ChangeRequestSection>

      {/* Meter access section */}
      <ChangeRequestSection id="meterAccessSection" visible={true}>
        <FormFieldLayout multipleFields>
          <Heading variant="h4">Meter access</Heading>
          <ReadOnlyTextField
            label="Customer Consultation Required"
            value={values.meterAccess.meterBehindGate === "yes" ? "Yes" : "No"}
          />
          {values.meterAccess.accessDetails && (
            <ReadOnlyTextField
              label="Access details"
              value={values.meterAccess.accessDetails}
            />
          )}
        </FormFieldLayout>
      </ChangeRequestSection>

      {/* Solar installation details section */}
      <ChangeRequestSection
        id="solarInstallationDetailsSection"
        visible={!!values.solarInstallationDetails.installationDate}
      >
        <ReadOnlyFieldWrapper value={values.solarInstallationDetails}>
          <FormFieldLayout multipleFields>
            {showFields.showSolarInstallationDetails && (
              <Heading variant="h4">Solar installation details</Heading>
            )}
            {showFields.showInstallationDate && (
              <ReadOnlyTextField
                label="Date panels were installed"
                value={
                  values.solarInstallationDetails.installationDate
                    ? values.solarInstallationDetails.installationDate.toLocaleDateString()
                    : ""
                }
              />
            )}
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>
      </ChangeRequestSection>

      {/* Supporting documents section */}
      <ChangeRequestSection
        id="supportingDocumentsSection"
        visible={sectionVisibility.supportingDocumentsSection}
      >
        <ReadOnlyFieldWrapper value={values.supportingDocuments}>
          <FormFieldLayout multipleFields>
            <Heading variant="h4">Supporting documents</Heading>

            {showFields.showRexId && values.supportingDocuments.rexId && (
              <ReadOnlyTextField
                label="REX ID"
                value={values.supportingDocuments.rexId}
              />
            )}
            {showFields.showCoesReferenceNumber &&
              values.supportingDocuments.coesReferenceNumber && (
                <ReadOnlyTextField
                  label="CoES reference number"
                  value={values.supportingDocuments.coesReferenceNumber}
                />
              )}
            {showFields.showCoesFiles &&
              values.coesFiles?.map((attachment) => (
                <ReadOnlyUploadedFile
                  key={attachment.attachmentId}
                  attachment={attachment}
                />
              ))}
            {showFields.showEwrFiles &&
              values.ewrFiles?.map((attachment) => (
                <ReadOnlyUploadedFile
                  key={attachment.attachmentId}
                  attachment={attachment}
                />
              ))}
            {values.networkFormNumber && (
              <ReadOnlyTextField
                label="Distributor request ID"
                value={values.networkFormNumber}
              />
            )}
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>
      </ChangeRequestSection>
      <FormFieldLayout>
        <RequestorInfo
          requesterName={values.requester || ""}
          submittedDateTime={values.submittedDateTime || ""}
        />
      </FormFieldLayout>
    </Stack>
  );
};

const ChangeRequestSection: React.FC<{
  id: string;
  children: React.ReactNode;
  visible: boolean;
}> = ({ id, children, visible }) => {
  if (!visible) {
    return null;
  }
  return (
    <Stack
      space={{
        xs: "xlarge",
        md: "xxlarge",
      }}
      id={id}
    >
      {children}
    </Stack>
  );
};

export default ChangeRequestElectricityBlockReadonly;
